/**
 * @description 带占位和默认图回退的背景加载方式
 * @author wayou
 */

import React, { Component, PropsWithChildren } from 'react';
import imageApi from '@umu/umu-image-handle';
import { Modal } from '@umu/umu-design';
import { BG_PLACEHOLDER } from '../../images';
import './UMUBg.scss';
import { classSet } from '@umu/umu-dom';
import { isInApp, isInMobile } from '@umu/umu-ua';
const { confirm } = Modal;

interface IUMUBgProps extends PropsWithChildren<{}> {
    clsName: string; // 自定义类名
    bgSrc: string; // 图片地址
    isOriginalScale?: boolean; // 是否保持原图比例
    previewControl?: boolean; // 允许图片最大化展示
    customPreviewIcons?: React.ReactNode; // 自定义图片最大化展示的图标
    fallbackSrc?: string; // 自定义默认图
    style?: { [name: string]: string | number }; // 外部自定义样式
    onClick?: (e?: React.MouseEvent<HTMLDivElement>) => boolean; // 点击事件 返回为true 进行图片预览
}

interface IUMUBgState {
    defaultImgCls: string;
    bgStyle: {
        backgroundImage: string;
    };
}

class UMUBg extends Component<IUMUBgProps, IUMUBgState> {
    public static defaultProps: Partial<IUMUBgProps> = {
        clsName: '',
        bgSrc: '',
        fallbackSrc: BG_PLACEHOLDER,
        style: {},
    };

    private _isMounted: boolean = false;

    constructor(props: IUMUBgProps) {
        super(props);

        this.lazyloadImg = this.lazyloadImg.bind(this);

        const defaultBg = this.props.fallbackSrc || BG_PLACEHOLDER;

        this.state = {
            defaultImgCls: 'umu-img-default',
            bgStyle: {
                backgroundImage: 'url(' + defaultBg + ')',
            },
        };
    }
    public componentDidUpdate(prevProps: Readonly<IUMUBgProps>): void {
        if (prevProps.bgSrc !== this.props.bgSrc) {
            this.lazyloadImg();
        }
    }
    public lazyloadImg() {
        const { isOriginalScale } = this.props;
        this._isMounted = true;
        const img = new Image();
        img.onload = () => {
            if (!this._isMounted) {
                return;
            }
            const bgStyle: any = {
                backgroundImage: 'url(' + imageApi.process(this.props.bgSrc) + ')',
            };
            if (isOriginalScale) {
                const scale = isOriginalScale ? img.width / img.height : 1;
                bgStyle['paddingBottom'] = `${(1 / scale) * 100}%`;
            }

            this.setState({
                bgStyle,
                defaultImgCls: '',
            });
        };
        img.src = this.props.bgSrc;
    }

    public UNSAFE_componentWillReceiveProps(nextProps: IUMUBgProps) {
        if (nextProps.bgSrc !== this.props.bgSrc) {
            this.lazyloadImg();
        }
    }

    public componentDidMount() {
        this.lazyloadImg();
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }
    private onPreview = (e?: React.MouseEvent<HTMLDivElement>) => {
        e?.stopPropagation();
        const { clsName, style } = this.props;
        const state = this.state;
        const eleStyle = Object.assign({}, style, state.bgStyle);
        const dialog = confirm({
            centered: true,
            closable: false,
            width: '9.6rem',
            // 背景透明
            bodyStyle: { backgroundColor: 'transparent' },
            wrapClassName: 'umu-bg-preview-modal',
            content: (
                <div
                    className={`umu-bg ${state.defaultImgCls} ${clsName}`}
                    style={eleStyle}
                    onClick={() => dialog.destroy()}
                />
            ),
            maskClosable: true,
            footer: null,
        } as any);
    };
    private renderPreviewControl() {
        const { previewControl, customPreviewIcons } = this.props;
        if (!previewControl) {
            return null;
        }
        return (
            <div
                className={classSet({
                    'umu-bg-preview': true,
                    'show-always': isInApp() || isInMobile(),
                })}
                onClick={this.props.onClick}
            >
                {customPreviewIcons || (
                    <div className="umu-bg-preview-icon" onClick={this.onPreview}>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="32" height="32" rx="3.2" fill="black" fillOpacity="0.8" />
                            <path
                                d="M20.7998 6.3999H25.5998V11.1999"
                                stroke="white"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M25.5998 20.7998V25.5998H20.7998"
                                stroke="white"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M11.1999 25.5998H6.3999V20.7998"
                                stroke="white"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M6.3999 11.1999V6.3999H11.1999"
                                stroke="white"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                )}
            </div>
        );
    }
    private onImgClick = (e?: React.MouseEvent<HTMLDivElement>) => {
        const { onClick } = this.props;
        if (onClick) {
            const isPreview = onClick(e);
            if (isPreview) {
                this.onPreview();
            }
        }
    };
    public render() {
        const props = this.props;
        const eleStyle = Object.assign({}, this.props.style, this.state.bgStyle);
        return (
            <div
                className={'umu-bg ' + this.state.defaultImgCls + ' ' + props.clsName}
                style={eleStyle}
                onClick={this.onImgClick}
            >
                {props.children}
                {this.renderPreviewControl()}
            </div>
        );
    }
}

export default UMUBg;
